import { slEntryFactory, ISLEntry, ISLCreateHeader } from "../../services/SLEntryFactory";
import { postSLEntry, submittedEntryForReview, sendToPosterRequest, validateEntryToSap } from "./SLCreateRequests";
import { AxiosResponse } from "axios";
import { IValidationMessage } from "../../schemas/ISLEntryResponse";
import { ISLCreateHeaderRef } from "./SLCreateHeader";
import { ISLCreateLineItemsGridRef } from "./SLCreateLineItems/SLCreateLineItems";
import { ISLCreateCommentFormRef } from "./SLCreateComments";
import { ISLCreatePostersReviewersRef } from "./SLCreatePostersReviewers";
import { IAttachmentEntryFormRef } from "../Attachments";
import { IAttachment } from "../../schemas/SLAttachment";
import { InvalidEntryError, SLEntriesError, RuntimeError } from "../../services/ErrorHelpers";
import { config } from "../../config";
import { SubledgerEndpoints } from "../../SubledgerEndpoints";
import { actionsClientCalls } from "../SLActionsManager/SLActionManagerRequests";
import { IUserProviderState } from "../../contexts/UserContext";
import { ISLEntryResponse } from "./SLCreateSchemas";

function parseEntryId(location?: string) {
  if (location === undefined) {
    return undefined;
  }
  const locationArray = location.split("/");
  const id = parseInt(locationArray[6], 10);
  if (isNaN(id)) {
    return undefined;
  }
  return id;
}

function calculateDebitCreditAndTotal(header: ISLCreateHeader, lineItems: string[][]) {
  let credit = 0;
  let debit = 0;
  for (const lineItem of lineItems) {
    if (!isNaN(Number(lineItem[10]))) {
      if (Number(lineItem[10]) > 0) {
        debit += Number(lineItem[10]);
      } else {
        credit += Number(lineItem[10]);
      }
    }
  }
  const totalAmount: number = credit + debit;
  return {
    debit: debit.toString(),
    credit: credit.toString(),
    totalAmount: totalAmount.toString()
  };
}

function createSlEntry(
  header: ISLCreateHeader,
  lineItems: string[][],
  attachments: IAttachment[],
  newComment: string,
  posters: string
): ISLEntry {
  const { debit, credit, totalAmount } = calculateDebitCreditAndTotal(header, lineItems);
  return {
    companyCode: header.companyCode,
    invoiceDate: header.invoiceDate,
    postingDate: header.postingDate,
    translationDate: header.translationDate,
    reference: header.reference,
    documentType: header.documentType,
    credit,
    debit,
    amount: totalAmount,
    currency: header.currencyCode,
    headerText: header.description,
    purpose: header.purpose,
    lineItemsList: lineItems,
    newUserComment: newComment,
    attachmentsList: attachments,
    additionalPoster: posters,
    rowVer: header.rowVer as ArrayBuffer
  };
}

export function assembleSLEntry(
  headerRef: React.RefObject<ISLCreateHeaderRef>,
  lineItemsRef: React.RefObject<ISLCreateLineItemsGridRef>,
  commentsRef: React.RefObject<ISLCreateCommentFormRef>,
  attachmentsRef: React.RefObject<IAttachmentEntryFormRef>,
  postersRef: React.RefObject<ISLCreatePostersReviewersRef>
): {
  entry: ISLEntry;
} {
  if (
    headerRef.current &&
    lineItemsRef.current &&
    commentsRef.current &&
    attachmentsRef.current &&
    postersRef.current
  ) {
    const header = headerRef.current.getHeader();
    const lineItems = lineItemsRef.current.getLineItems();
    const attachments = attachmentsRef.current.getAttachments();
    const newComment = commentsRef.current.getNewComment();
    const posters = postersRef.current.getAdditionalPosters();
    return {
      entry: createSlEntry(header.result, lineItems, attachments, newComment, posters)
    };
  } else {
    // A SubComponent needs to crash to reach here
    throw new Error("Ref Mount Error");
  }
}

function formatValidationErrors(resultMessages: IValidationMessage[]): string[] {
  const messages = resultMessages.map((payload) => payload.errorMessage);
  return messages;
}

function handleErrorFromAxios(response: AxiosResponse<IValidationMessage[]>) {
  // We catch the possible API Error, SLEntries Returns an array of objects with ErrorMessages in it
  const postStatus = Math.floor(response.status / 100);
  if (postStatus === 4) {
    // 400 level
    const errorMessages = formatValidationErrors(response.data);
    return new SLEntriesError(["Invalid input.", ...errorMessages]);
  }
  return new SLEntriesError(["Internal server error. Please try again later."]);
}

export async function saveEntry(
  slEntryId: number,
  slEntry: ISLEntry,
  getTokenFn: IUserProviderState["accessToken"]
): Promise<{
  messages: string[];
  entry: ISLEntryResponse;
}> {
  try {
    const headerBody = slEntryFactory.getSLEntryPostBody(slEntry, slEntryId);
    if (!headerBody) {
      throw new InvalidEntryError("Invalid entry - changes not saved.");
    }
    const result = await postSLEntry(headerBody, getTokenFn);
    const entry = result.data as ISLEntryResponse;
    const entryId =
      entry.slEntryId === undefined
        ? parseEntryId(result.headers.location) // to be deprecated
        : entry.slEntryId;

    return {
      messages: [`Successfully saved entry. Entry ID is ${entryId}.`],
      entry
    };
  } catch (error) {
    if (error instanceof SLEntriesError) {
      throw new SLEntriesError(["Unable to save.", ...error.messages]);
    } else {
      // Some other non-axios error
      throw new SLEntriesError(["Unable to save."]);
    }
  }
}

export async function postEntry(
  slEntryId: number,
  getTokenFn: IUserProviderState["accessToken"]
): Promise<AxiosResponse<any>> {
  const sapResult = await submittedEntryForReview(slEntryId, getTokenFn);
  if (sapResult !== undefined) {
    if (sapResult.data !== "") {
      return sapResult;
    } else {
      throw new SLEntriesError(["SLEntries returned no data."]);
    }
  } else {
    throw new RuntimeError("Posting to endpoint returned no data.");
  }
}

export async function sendToPoster(
  slEntryId: number,
  getTokenFn: IUserProviderState["accessToken"]
): Promise<{
  messages: string[];
}> {
  try {
    await sendToPosterRequest(slEntryId, getTokenFn);
    return {
      messages: [`Successfully sent entry to poster. Entry ID is ${slEntryId}.`]
    };
  } catch (err) {
    if (err instanceof SLEntriesError) {
      throw new SLEntriesError(["Unable to send to poster.", ...err.messages]);
    } else {
      throw new SLEntriesError(["Unable to send to poster."]);
    }
  }
}

export async function validate(
  slEntryId: number,
  getTokenFn: IUserProviderState["accessToken"]
): Promise<{
  messages: string[];
}> {
  try {
    if (slEntryId === 0) {
      throw new InvalidEntryError("Entry must be saved first.");
    }
    const result = await validateEntryToSap(slEntryId, getTokenFn);
    const apiMessages = (result.data as IValidationMessage[]).map((item) => item.errorMessage);
    if (apiMessages.length !== 0) {
      throw new SLEntriesError(apiMessages);
    }
    // apiMessages will be empty if there are no errors
    return {
      messages: [`Completed validation of entry ${slEntryId}.`]
    };
  } catch (possibleAPIError) {
    if (possibleAPIError.response) {
      const response = possibleAPIError.response as AxiosResponse<IValidationMessage[]>;
      // the below will handle the error and rethrow it as something more manageable
      throw handleErrorFromAxios(response);
    } else {
      // Some other non-axios error
      throw possibleAPIError;
    }
  }
}

const basicRequest = async (
  SelectedSlEntryIds: number[],
  endpoint: string,
  getTokenFn: IUserProviderState["accessToken"],
  Comments?: string
) => {
  const url = config.get("subledgerUrl") + SubledgerEndpoints.slActions + endpoint;
  try {
    let payload = {
      SelectedSlEntryIds
    };
    if (Comments) {
      payload = { ...payload, Comments } as any;
    }
    await actionsClientCalls(payload, url, getTokenFn);
  } catch (possibleAPIError) {
    if (possibleAPIError.response) {
      const response = possibleAPIError.response as AxiosResponse<IValidationMessage[]>;
      // the below will handle the error and rethrow it as something more manageable
      throw handleErrorFromAxios(response);
    } else {
      // Some other non-axios error
      throw possibleAPIError;
    }
  }
};

export async function approve(
  slEntryId: number,
  getTokenFn: IUserProviderState["accessToken"]
): Promise<{
  messages: string[];
}> {
  await basicRequest([slEntryId], SubledgerEndpoints.slActionsApproveEntries, getTokenFn);
  return {
    messages: [`Completed Approval of entry ${slEntryId}.`]
  };
}
export async function needsClarification(
  slEntryId: number,
  getTokenFn: IUserProviderState["accessToken"],
  userComment: string
): Promise<{
  messages: string[];
}> {
  await basicRequest([slEntryId], SubledgerEndpoints.slActionsNeedsClarification, getTokenFn, userComment);
  return {
    messages: [`Sent entry ${slEntryId} as Needs Clarification.`]
  };
}
export async function recall(
  slEntryId: number,
  getTokenFn: IUserProviderState["accessToken"],
  jeStatus: number
): Promise<{
  messages: string[];
}> {
  const endpoint =
    jeStatus === 2
      ? SubledgerEndpoints.slActionsAuthorIsRecallingDraft
      : SubledgerEndpoints.slActionsPosterIsRecallingSignedOffRequested; // jeStatus = 10 if this happens, otherwise button should be disabled
  await basicRequest([slEntryId], endpoint, getTokenFn);
  return {
    messages: [`Recalled entry ${slEntryId}.`]
  };
}
